import type { DiscordEmbed } from './types';
import { decimalToHex } from './types';
import { DiscordMarkdown } from './markdown';

interface Props {
  embed: DiscordEmbed;
  authorAvatarUrl?: string;
  authorDisplayName?: string;
  className?: string;
}

const DEFAULT_AVATAR =
  'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 40 40"><circle cx="20" cy="20" r="20" fill="%235865F2"/></svg>';

export function DiscordEmbedPreview({
  embed,
  authorAvatarUrl,
  authorDisplayName = 'Raynex Bot',
  className,
}: Props) {
  const colorHex = embed.color !== undefined ? decimalToHex(embed.color) : '#5865F2';

  return (
    <div
      className={
        'rounded-md bg-[#36393f] p-4 text-[#dcddde] ' +
        '[font-family:Whitney,Helvetica_Neue,Helvetica,Arial,sans-serif] ' +
        (className ?? '')
      }
    >
      <div className="flex items-start gap-3">
        <img
          src={authorAvatarUrl ?? DEFAULT_AVATAR}
          alt=""
          className="h-10 w-10 shrink-0 rounded-full bg-[#5865F2]"
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-semibold text-white">{authorDisplayName}</span>
            <span className="rounded bg-[#5865F2] px-1 text-[10px] font-semibold text-white">
              BOT
            </span>
            <span className="text-xs text-[#a3a6aa]">today</span>
          </div>
          <EmbedBody embed={embed} colorHex={colorHex} />
        </div>
      </div>
    </div>
  );
}

function EmbedBody({ embed, colorHex }: { embed: DiscordEmbed; colorHex: string }) {
  const hasContent =
    embed.title ||
    embed.description ||
    embed.author ||
    embed.footer ||
    embed.image ||
    embed.thumbnail ||
    (embed.fields && embed.fields.length > 0);

  if (!hasContent) {
    return <div className="mt-1 text-sm italic text-[#a3a6aa]">Empty embed</div>;
  }

  return (
    <div
      className="mt-1 grid max-w-[520px] grid-cols-[4px_1fr] overflow-hidden rounded-[4px] bg-[#2f3136]"
      style={{ borderLeft: `4px solid ${colorHex}` }}
    >
      <div />
      <div className="flex flex-col gap-2 p-3">
        {embed.author && (
          <div className="flex items-center gap-2 text-sm font-medium text-white">
            {embed.author.icon_url && (
              <img src={embed.author.icon_url} alt="" className="h-6 w-6 rounded-full" />
            )}
            {embed.author.url ? (
              <a href={embed.author.url} className="hover:underline">
                {embed.author.name}
              </a>
            ) : (
              <span>{embed.author.name}</span>
            )}
          </div>
        )}

        {embed.title && (
          <div className="font-semibold text-white">
            {embed.url ? (
              <a href={embed.url} className="text-[#00b0f4] hover:underline">
                <DiscordMarkdown text={embed.title} inline />
              </a>
            ) : (
              <DiscordMarkdown text={embed.title} inline />
            )}
          </div>
        )}

        {embed.description && (
          <div className="text-sm leading-snug text-[#dcddde]">
            <DiscordMarkdown text={embed.description} />
          </div>
        )}

        {embed.fields && embed.fields.length > 0 && (
          <div className="grid grid-cols-3 gap-2">
            {embed.fields.map((f, i) => (
              <div key={i} className={f.inline ? 'col-span-1 min-w-[80px] flex-1' : 'col-span-3'}>
                <div className="text-xs font-semibold text-white">
                  {f.name ? <DiscordMarkdown text={f.name} inline /> : ' '}
                </div>
                <div className="text-sm leading-snug text-[#dcddde]">
                  {f.value ? <DiscordMarkdown text={f.value} /> : ' '}
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="flex gap-3">
          {embed.image && (
            <img
              src={embed.image.url}
              alt=""
              className="mt-1 max-h-[300px] rounded-md object-contain"
            />
          )}
          {embed.thumbnail && (
            <img
              src={embed.thumbnail.url}
              alt=""
              className="mt-1 ml-auto max-h-[80px] max-w-[80px] rounded-md object-contain"
            />
          )}
        </div>

        {(embed.footer || embed.timestamp) && (
          <div className="mt-1 flex items-center gap-2 text-xs text-[#a3a6aa]">
            {embed.footer?.icon_url && (
              <img src={embed.footer.icon_url} alt="" className="h-5 w-5 rounded-full" />
            )}
            {embed.footer?.text && <span>{embed.footer.text}</span>}
            {embed.footer?.text && embed.timestamp && <span>•</span>}
            {embed.timestamp && (
              <time dateTime={embed.timestamp}>
                {new Date(embed.timestamp).toLocaleString('en-US', {
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </time>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
